// A process-wide cache of the /mcc section's three heavy fetches -- the manufacturing-line listing, each
// manufacturing line's best score, and the picked plan's solve -- so a fresh ManufacturingLineLists instance
// reuses what an identical fetch just produced instead of re-running the work. This is what stops the section
// tab from re-listing, re-scoring and re-solving everything the guided setup wizard just did the moment the
// wizard closes: the wizard and the tab are SEPARATE ManufacturingLineLists instances, so the tab mounting
// fresh would otherwise fire all three fetches again and double the user's wait.
//
// Keyed by the request URL alone, because the URL now carries EVERY input the response varies on:
//   - The section index, the filter checkboxes (Bobbin as bobbin_enabled, and the printing- / die-cutting-machine
//     ticks as disabled_filters[] plus the *_type[] params), the area-ratio cap, and -- for the solve and best
//     score -- the picked plan.
//   - The quantity (count). The client sends count as a URL param on all three fetches, and the server scores /
//     solves against that param (falling back to the quote's own count only when it is absent). So client and
//     server always agree on the quantity, AND count is in the key for free -- a quantity change mints a
//     different URL and misses. (Sending count also fixes a race: the count field persists user_quote.count
//     asynchronously, so a server that read the quote could score against a not-yet-saved quantity.)
// Nothing else varies the result within a /mcc session: the material / dieline is fixed (the Dieline tab is
// read-only; changing it means leaving /mcc, which reloads the page and drops this module's Map with it).
//
// Modelled on SolutionScoreCache -- a plain module-level Map that lives for the page session.

const responseCache = new Map();

// The canonical key: the URL with its query parameters sorted, so an identical request keys the same regardless
// of the order the params happened to be appended in. Sorting is a lossless canonicalisation (two different
// input sets can never collapse to one key), so a cache HIT always means a truly identical request; a differing
// param order can at worst cause a harmless MISS, never a wrong hit.
const canonicalUrl = (url) => {
  const [path, query = ''] = url.split('?');
  if (!query) { return path; }
  const params = new URLSearchParams(query);
  params.sort();
  return `${path}?${params.toString()}`;
};

export const hasCachedResponse = (url) => responseCache.has(canonicalUrl(url));
export const getCachedResponse = (url) => responseCache.get(canonicalUrl(url));
export const setCachedResponse = (url, data) => { responseCache.set(canonicalUrl(url), data); };
export const clearResponseCache = () => { responseCache.clear(); };

// Fetch JSON through the cache: a cached response resolves immediately (no network, no request-queue slot);
// otherwise fetch, parse (null on a non-OK response, which is NOT cached -- a transient failure must not stick),
// store on success, and return it. The abort signal is honoured for a live fetch; a cache hit has nothing in
// flight to abort. A rejected fetch / json() propagates -- the callers own their error handling (each wraps
// this in its own .catch) and a rejection never reaches setCachedResponse, so nothing bad is cached.
export const fetchJsonCached = (url, options = {}) => {
  if (hasCachedResponse(url)) {
    return Promise.resolve(getCachedResponse(url));
  }
  return fetch(url, options)
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      if (data !== null) { setCachedResponse(url, data); }
      return data;
    });
};
